import { useCallback, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import type { AuthTenant } from '@/entities/auth';
import {
  rememberReturnTo,
  returnPathFrom,
  takeReturnTo,
} from '@/shared/lib/returnTo';

import { AuthShell } from './AuthShell';
import { EmailStep } from './login/EmailStep';
import { MfaStep } from './login/MfaStep';
import { PasswordStep } from './login/PasswordStep';
import { TenantList } from './login/TenantList';

/**
 * Шаги входа. Живут в состоянии страницы, а не в адресе: mfa_token и список
 * компаний в URL не кладём, а после перезагрузки вход и так начинается заново
 * — mfa_token живёт всего 5 минут.
 */
type Step =
  | { name: 'email' }
  | { name: 'tenant'; tenants: AuthTenant[] }
  | { name: 'password'; tenants: AuthTenant[]; tenant: AuthTenant }
  | {
      name: 'mfa';
      tenants: AuthTenant[];
      tenant: AuthTenant;
      mfaToken: string;
      issuedAt: number;
    };

/**
 * Вход по инструкции бэка: почта → компания → пароль или Google → код 2FA.
 * Google уводит со страницы и возвращает на `/auth/google/callback`
 * (GoogleCallbackPage).
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Путь вместе с query: иначе теряется, например, ?next= у /calls/open.
  const from = returnPathFrom(location.state?.from);

  // Вход через Google уходит со страницы — state не переживёт, запоминаем отдельно.
  useEffect(() => {
    rememberReturnTo(from);
  }, [from]);

  const [email, setEmail] = useState('');
  const [step, setStep] = useState<Step>({ name: 'email' });

  // Без оптимистики: сессию проставляет onSuccess мутации, здесь только переход.
  const goToApp = useCallback(() => {
    toast.success('С возвращением');
    takeReturnTo();
    navigate(from, { replace: true });
  }, [from, navigate]);

  const toEmail = () => setStep({ name: 'email' });

  if (step.name === 'tenant') {
    const { tenants } = step;

    return (
      <AuthShell
        title="Выберите компанию"
        subtitle={email}
        footer={
          <button
            type="button"
            onClick={toEmail}
            className="font-medium text-[var(--primary)] hover:underline"
          >
            Другая почта
          </button>
        }
      >
        <TenantList
          tenants={tenants}
          onSelect={(tenant) => setStep({ name: 'password', tenants, tenant })}
        />
      </AuthShell>
    );
  }

  if (step.name === 'password') {
    const { tenants, tenant } = step;

    return (
      <AuthShell title="Вход" subtitle={tenant.name}>
        <PasswordStep
          email={email}
          tenant={tenant}
          onChangeEmail={toEmail}
          onChangeTenant={
            tenants.length > 1
              ? () => setStep({ name: 'tenant', tenants })
              : undefined
          }
          onMfaRequired={(mfaToken) =>
            setStep({
              name: 'mfa',
              tenants,
              tenant,
              mfaToken,
              issuedAt: Date.now(),
            })
          }
          onLoggedIn={goToApp}
        />
      </AuthShell>
    );
  }

  if (step.name === 'mfa') {
    const { tenants, tenant, mfaToken, issuedAt } = step;
    const toPassword = () => setStep({ name: 'password', tenants, tenant });

    return (
      <AuthShell
        title="Код подтверждения"
        subtitle="Введите код из приложения-аутентификатора"
      >
        <MfaStep
          mfaToken={mfaToken}
          issuedAt={issuedAt}
          onLoggedIn={goToApp}
          onExpired={() => {
            toast.error('Время на ввод кода истекло, войдите заново');
            toPassword();
          }}
          onRestart={toPassword}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Вход"
      subtitle="Войдите в аккаунт DOIT"
      footer={
        <>
          Нет учётной записи?{' '}
          <Link
            to="/register"
            className="font-medium text-[var(--primary)] hover:underline"
          >
            Зарегистрироваться
          </Link>
        </>
      }
    >
      <EmailStep
        defaultEmail={email}
        onFound={(foundEmail, tenants) => {
          setEmail(foundEmail);
          // Одна компания — сразу к паролю, несколько — пользователь выбирает.
          const [only] = tenants;
          setStep(
            tenants.length === 1 && only
              ? { name: 'password', tenants, tenant: only }
              : { name: 'tenant', tenants }
          );
        }}
      />
    </AuthShell>
  );
}
